import { Link } from 'react-router-dom';
import PageLayout from './PageLayout';

/* ── Feature cards (top grid) ── */
const features = [
  {
    title: 'Live Heatmaps',
    desc: 'Exposure levels rendered across price and time, updating as trades come in.',
    color: '#3b82f6',
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <rect x="4" y="4" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.3" />
        <rect x="13" y="4" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.5" />
        <rect x="22" y="4" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.2" />
        <rect x="4" y="13" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.6" />
        <rect x="13" y="13" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.9" />
        <rect x="22" y="13" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.7" />
        <rect x="4" y="22" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.2" />
        <rect x="13" y="22" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.5" />
        <rect x="22" y="22" width="7" height="7" rx="1" fill="#3b82f6" opacity="0.4" />
      </svg>
    ),
  },
  {
    title: 'Key Levels',
    desc: 'Automatically detected support, resistance, and regime-change zones derived from positioning data.',
    color: '#06b6d4',
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <line x1="4" y1="10" x2="32" y2="10" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="3 2" />
        <line x1="4" y1="18" x2="32" y2="18" stroke="#06b6d4" strokeWidth="2" />
        <line x1="4" y1="26" x2="32" y2="26" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="3 2" />
        <circle cx="18" cy="18" r="3" fill="none" stroke="#06b6d4" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    title: 'Volume & Flow',
    desc: 'See where volume concentrates, how trades are classified, and which direction flow is pushing.',
    color: '#8b5cf6',
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <rect x="5" y="22" width="5" height="10" rx="1" fill="#8b5cf6" opacity="0.5" />
        <rect x="12" y="16" width="5" height="16" rx="1" fill="#8b5cf6" opacity="0.7" />
        <rect x="19" y="8" width="5" height="24" rx="1" fill="#8b5cf6" />
        <rect x="26" y="18" width="5" height="14" rx="1" fill="#8b5cf6" opacity="0.6" />
        <path d="M7 20 L14 14 L21 6 L28 16" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5" />
      </svg>
    ),
  },
  {
    title: 'Multi-Underlying',
    desc: 'Full coverage of SPX index options, SPY ETF options, and ES futures options, switchable with one click.',
    color: '#f59e0b',
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <rect x="4" y="12" width="28" height="12" rx="6" stroke="#1e293b" strokeWidth="1.5" fill="#111520" />
        <circle cx="24" cy="18" r="4.5" fill="#f59e0b" />
        <text x="11" y="21" textAnchor="middle" fill="#64748b" fontSize="7" fontWeight="600">A</text>
        <text x="24" y="21" textAnchor="middle" fill="#111520" fontSize="7" fontWeight="700">B</text>
      </svg>
    ),
  },
];

/* ── Indicator deep-dive content ── */
const indicators = [
  {
    tag: 'GEX', color: '#3b82f6', label: 'Gamma Exposure',
    body: 'When dealers are long gamma, they naturally buy dips and sell rallies \u2014 dampening volatility and creating support and resistance. When they\u2019re short gamma, they have to hedge in the same direction as price, which amplifies moves. The heatmap shows this exposure building across strikes in real time, so you can spot the regime shift before it shows up in price.',
  },
  {
    tag: 'DEX', color: '#8b5cf6', label: 'Delta Exposure',
    body: 'Delta exposure shows the directional positioning dealers carry at each strike. When DEX is large and positive at a level, dealers are net long there and will sell into rallies to stay hedged. When it\u2019s negative, they need to buy. These flows create predictable pressure zones that persist throughout the session.',
  },
  {
    tag: 'VEX', color: '#f59e0b', label: 'Vanna Exposure',
    body: 'Vanna captures how dealer delta shifts when implied volatility moves. As IV compresses, positive vanna forces dealers to add long delta \u2014 creating a tailwind for rallies. When vol spikes, the effect reverses. This is a second-order flow that\u2019s invisible on a standard price chart but drives some of the largest intraday moves.',
  },
  {
    tag: 'CEX', color: '#10b981', label: 'Charm Exposure',
    body: 'Charm tracks how dealer delta changes simply from the passage of time. As same-day options approach expiry, their delta sensitivity shifts rapidly and dealers have to keep adjusting. This effect concentrates at specific strikes and is especially powerful in the final hours of the session.',
  },
];

/* ── Reusable styles ── */
const sectionWrap = {
  maxWidth: '800px', margin: '0 auto', padding: '0 2rem',
};

const divider = {
  width: '48px', height: '2px', background: '#1e293b',
  margin: '4rem auto', borderRadius: '1px',
};

export default function LandingPage() {
  return (
    <PageLayout>

      {/* ────── Hero ────── */}
      <section style={{
        textAlign: 'center', padding: '6rem 2rem 4rem',
        maxWidth: '750px', margin: '0 auto', position: 'relative',
      }}>
        <div style={{
          position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '400px',
          background: 'radial-gradient(ellipse, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <h1 style={{
          fontSize: 'clamp(2.25rem, 5vw, 3.25rem)', fontWeight: 700,
          margin: 0, letterSpacing: '-0.03em', lineHeight: 1.15,
          position: 'relative',
        }}>
          Live options flow,
          <br />
          <span style={{ color: '#3b82f6' }}>charted in real time</span>
        </h1>
        <p style={{
          color: '#64748b', fontSize: '1.1rem', marginTop: '1.5rem',
          lineHeight: 1.7, position: 'relative',
        }}>
          Options trades, dealer Greeks, open interest levels, volume, and key
          price zones &mdash; all streaming live to a single chart.
        </p>
        <Link to="/chart" style={{
          display: 'inline-block', marginTop: '2.5rem',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: '#fff', border: 'none', borderRadius: '8px',
          padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 600,
          textDecoration: 'none', position: 'relative',
          boxShadow: '0 4px 20px rgba(59,130,246,0.25)',
        }}>
          View Charts
        </Link>
      </section>

      {/* ────── Feature cards ────── */}
      <section style={{
        maxWidth: '920px', margin: '0 auto', padding: '1rem 2rem 0',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.25rem',
      }}>
        {features.map((f) => (
          <div key={f.title} style={{
            background: '#111520', borderRadius: '10px', padding: '1.75rem',
            border: '1px solid #1e293b',
            display: 'flex', flexDirection: 'column', gap: '0.875rem',
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '10px',
              background: `${f.color}10`, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {f.icon}
            </div>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#e2e8f0' }}>
              {f.title}
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', lineHeight: 1.55 }}>
              {f.desc}
            </p>
          </div>
        ))}
      </section>

      <div style={divider} />

      {/* ────── Why it matters ────── */}
      <section style={{ ...sectionWrap, textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.75rem' }}>
          Why It Matters
        </h2>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.5rem', textAlign: 'left',
        }}>
          {[
            { title: 'Anticipate hedging flows', text: 'See where dealers will need to hedge before price gets there. These exposure levels act as magnets and barriers for intraday moves.' },
            { title: 'Identify regime shifts', text: 'Gamma flip zones mark the line between mean-reverting and trending conditions. Knowing which regime you\u2019re in changes how you trade.' },
            { title: 'Model-driven levels', text: 'Levels are derived from options open interest and a calibrated volatility surface \u2014 not drawn by hand. The math does the work.' },
          ].map((v) => (
            <div key={v.title} style={{
              background: '#111520', borderRadius: '10px', padding: '1.5rem',
              border: '1px solid #1e293b',
            }}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 600, color: '#e2e8f0' }}>
                {v.title}
              </h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', lineHeight: 1.6 }}>
                {v.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div style={divider} />

      {/* ────── Indicator deep-dive ────── */}
      <section style={sectionWrap}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', textAlign: 'center' }}>
          Understanding the Indicators
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', textAlign: 'center', marginBottom: '2.5rem', lineHeight: 1.6 }}>
          Each indicator reveals a different dimension of how dealers are positioned and where they need to hedge.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {indicators.map((ind) => (
            <div key={ind.tag} style={{
              background: '#111520', borderRadius: '10px', padding: '1.75rem',
              border: '1px solid #1e293b', borderLeft: `3px solid ${ind.color}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <span style={{
                  display: 'inline-block', padding: '0.15rem 0.6rem', borderRadius: '4px',
                  fontSize: '0.75rem', fontWeight: 700, color: ind.color,
                  background: `${ind.color}15`, letterSpacing: '0.04em',
                }}>
                  {ind.tag}
                </span>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0' }}>
                  {ind.label}
                </span>
              </div>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.7 }}>
                {ind.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div style={divider} />

      {/* ────── How it works ────── */}
      <section style={{ ...sectionWrap, textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>
          How It Works
        </h2>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem', textAlign: 'left',
        }}>
          {[
            { step: '01', title: 'Ingest', text: 'Live options trades for SPX, SPY, and ES futures options are streamed tick-by-tick with full quote context, giving us the bid-ask spread needed to classify each trade as a buy or sell.' },
            { step: '02', title: 'Compute', text: 'Every trade is priced through a calibrated volatility surface to extract gamma, delta, vanna, and charm. Positions are tracked cumulatively across the session.' },
            { step: '03', title: 'Visualize', text: 'Exposure is rendered as a heatmap across strike and time, updating every few seconds. Baseline levels from prior-day open interest are projected forward to give you context before the session even opens.' },
          ].map((s) => (
            <div key={s.step} style={{
              background: '#111520', borderRadius: '10px', padding: '1.5rem',
              border: '1px solid #1e293b',
            }}>
              <span style={{
                fontSize: '1.5rem', fontWeight: 700, color: '#1e293b',
                display: 'block', marginBottom: '0.75rem', fontFamily: 'monospace',
              }}>
                {s.step}
              </span>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 600, color: '#e2e8f0' }}>
                {s.title}
              </h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', lineHeight: 1.6 }}>
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div style={divider} />

      {/* ────── CTA banner ────── */}
      <section style={{
        textAlign: 'center', padding: '3rem 2rem 4rem',
        maxWidth: '600px', margin: '0 auto',
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>
          Ready to see the flow?
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '2rem' }}>
          Live heatmaps update throughout the session. Historical data available for prior trading days.
        </p>
        <Link to="/chart" style={{
          display: 'inline-block',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: '#fff', border: 'none', borderRadius: '8px',
          padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 600,
          textDecoration: 'none',
          boxShadow: '0 4px 20px rgba(59,130,246,0.25)',
        }}>
          View Charts
        </Link>
        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
          <Link to="/terms" style={{ color: '#475569', fontSize: '0.8rem', textDecoration: 'none' }}>
            Terms of Use
          </Link>
          <Link to="/privacy" style={{ color: '#475569', fontSize: '0.8rem', textDecoration: 'none' }}>
            Privacy Policy
          </Link>
          <Link to="/contact" style={{ color: '#475569', fontSize: '0.8rem', textDecoration: 'none' }}>
            Contact
          </Link>
        </div>
      </section>

    </PageLayout>
  );
}
